// Configuration pour les types de documents nécessitant une page de détail "Planifier"

// Types de documents complexes qui affichent le bouton "Planifier"
export const COMPLEX_DOC_TYPES = [
  'identity',
  'passport', 
  'gray_card',
  'driver_license',
  'health',
  'residence_permit',
  'vehicle_insurance',
  'technical_control',
  'kbis',
  'professional_card',
  'work_permit',
] as const;

export type ComplexDocType = typeof COMPLEX_DOC_TYPES[number];

// Vérifie si un type de document est complexe
export function isComplexDocType(docType: string | null | undefined): boolean {
  if (!docType) return false;
  return COMPLEX_DOC_TYPES.includes(docType as ComplexDocType);
}

// Liens officiels par type de document
export const OFFICIAL_LINKS: Record<string, { label: string; url: string; description: string }> = {
  identity: { 
    label: "ANTS - Carte d'identité", 
    url: "https://ants.gouv.fr/demarches-en-ligne/carte-nationale-d-identite",
    description: "Pré-demande et prise de RDV en mairie"
  },
  passport: { 
    label: "ANTS - Passeport", 
    url: "https://ants.gouv.fr/demarches-en-ligne/passeport",
    description: "Pré-demande et prise de RDV en mairie"
  },
  gray_card: { 
    label: "ANTS - Carte grise", 
    url: "https://ants.gouv.fr/demarches-en-ligne/certificat-d-immatriculation",
    description: "Demande de certificat d'immatriculation"
  },
  driver_license: { 
    label: "ANTS - Permis de conduire", 
    url: "https://ants.gouv.fr/demarches-en-ligne/permis-de-conduire",
    description: "Renouvellement ou duplicata"
  },
  health: { 
    label: "Ameli - Carte Vitale", 
    url: "https://www.ameli.fr/assure/remboursements/etre-bien-rembourse/carte-vitale",
    description: "Mise à jour de la carte Vitale"
  },
  residence_permit: { 
    label: "Préfecture - Titre de séjour", 
    url: "https://www.service-public.fr/particuliers/vosdroits/N110",
    description: "Renouvellement du titre de séjour"
  },
  vehicle_insurance: { 
    label: "Assurance auto", 
    url: "https://www.service-public.fr/particuliers/vosdroits/F2628",
    description: "Informations sur l'assurance automobile"
  },
  technical_control: { 
    label: "Contrôle technique", 
    url: "https://www.securite-routiere.gouv.fr/les-medias/nos-publications/le-controle-technique",
    description: "Prendre RDV pour le contrôle technique"
  },
  kbis: { 
    label: "Infogreffe - Kbis", 
    url: "https://www.infogreffe.fr/",
    description: "Commander un extrait Kbis"
  },
  professional_card: { 
    label: "Carte professionnelle", 
    url: "https://www.service-public.fr/",
    description: "Renouvellement de carte professionnelle"
  },
  work_permit: { 
    label: "Autorisation de travail", 
    url: "https://www.service-public.fr/particuliers/vosdroits/F15898",
    description: "Demande d'autorisation de travail"
  },
};

// Tâches par défaut suggérées selon le type de document
export const DEFAULT_TASKS: Record<string, string[]> = {
  identity: [
    "Photo d'identité récente",
    "Justificatif de domicile < 1 an",
    "Ancienne carte d'identité",
    "Pré-demande ANTS complétée",
    "RDV en mairie pris"
  ],
  passport: [
    "Photo d'identité récente",
    "Justificatif de domicile < 1 an",
    "Ancien passeport",
    "Pré-demande ANTS complétée",
    "Timbre fiscal acheté",
    "RDV en mairie pris"
  ],
  gray_card: [
    "Ancienne carte grise",
    "Contrôle technique valide",
    "Justificatif de domicile",
    "Pièce d'identité"
  ],
  driver_license: [
    "Photo d'identité récente",
    "Justificatif de domicile",
    "Ancien permis de conduire",
    "Pré-demande ANTS"
  ],
  health: [
    "Carte Vitale actuelle",
    "Photo d'identité récente",
    "Attestation de droits"
  ],
  residence_permit: [
    "Passeport valide",
    "Photos d'identité",
    "Justificatif de domicile",
    "Justificatifs de ressources",
    "Ancien titre de séjour",
    "RDV préfecture pris"
  ],
  vehicle_insurance: [
    "Carte grise du véhicule",
    "Permis de conduire",
    "Relevé d'informations"
  ],
  technical_control: [
    "Carte grise du véhicule",
    "RDV centre de contrôle pris"
  ],
};

// Obtenir le lien officiel pour un type de document
export function getOfficialLink(docType: string | null | undefined): { label: string; url: string; description: string } | null {
  if (!docType) return null;
  return OFFICIAL_LINKS[docType] || null;
}

// Obtenir les tâches par défaut pour un type de document
export function getDefaultTasks(docType: string | null | undefined): string[] {
  if (!docType) return [];
  return DEFAULT_TASKS[docType] || [];
}
